import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Download, FileSpreadsheet, Loader2, Pencil, Plus, Search, Trash2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AGE_LABELS, ROLE_LABELS, SIDE_LABELS, initials } from "@/lib/format";
import {
  addGuest,
  deleteGuest,
  editGuest,
  bulkDeleteGuests,
  bulkUpdateGuests,
  errorMessage,
  guestExportUrl,
  guestTemplateUrl,
  importGuests,
} from "@/lib/api";
import { t, tf } from "@/i18n";

const FILTERS = [
  { id: "all", label: "guests.all" },
  { id: "answered", label: "guests.answered" },
  { id: "pending", label: "guests.pending" },
];

const BLANK = {
  first_name: "",
  last_name: "",
  email: "",
  side: "both",
  household: "",
  role: "none",
  age_band: "adult",
};

const SmallLabel = ({ children }) => (
  <Label className="text-[11px] uppercase tracking-wide text-[color:var(--muted-ink)]">{children}</Label>
);

export const GuestsTab = ({ weddingId, guests, onChanged, rsvpUrl }) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [newGuest, setNewGuest] = useState(BLANK);
  const [expanded, setExpanded] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const fileRef = useRef(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const onUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // so picking the same file twice still fires
    if (!file) return;
    setUploading(true);
    try {
      const report = await importGuests(weddingId, file);
      setImportReport(report);
      toast.success(tf("guests.imported", { added: report.added, updated: report.updated }));
      await onChanged();
    } catch (error) {
      setImportReport(null);
      toast.error(errorMessage(error, t("guests.cannotRead")));
    } finally {
      setUploading(false);
    }
  };

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return guests.filter((guest) => {
      if (needle && !`${guest.name} ${guest.email || ""}`.toLowerCase().includes(needle)) return false;
      if (filter === "answered") return Boolean(guest.answers);
      if (filter === "pending") return !guest.answers;
      return true;
    });
  }, [guests, query, filter]);

  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Acts on what is on screen, not on the whole list: ticking the box after a
  // search should select those results, which is what the label promises.
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((guest) => selected.has(guest.id));
  const toggleAllFiltered = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach((guest) => next.delete(guest.id));
      else filtered.forEach((guest) => next.add(guest.id));
      return next;
    });

  const runBulk = async (action, describe) => {
    setBulkBusy(true);
    try {
      const result = await action([...selected]);
      toast.success(describe(result));
      setSelected(new Set());
      await onChanged();
    } catch (error) {
      toast.error(errorMessage(error, t("guests.cannotApply")));
    } finally {
      setBulkBusy(false);
      setConfirmDelete(false);
    }
  };

  const submitNew = async (event) => {
    event.preventDefault();
    if (!newGuest.first_name.trim()) {
      toast.error(t("guests.firstNameNeeded"));
      return;
    }
    setAdding(true);
    try {
      await addGuest(weddingId, {
        name: `${newGuest.first_name.trim()} ${newGuest.last_name.trim()}`.trim(),
        email: newGuest.email.trim(),
        side: newGuest.side,
        household: newGuest.household.trim() || null,
        role: newGuest.role === "none" ? null : newGuest.role,
        age_band: newGuest.age_band,
      });
      setNewGuest(BLANK);
      toast.success(t("guests.added"));
      await onChanged();
    } catch (error) {
      toast.error(errorMessage(error, t("guests.cannotAdd")));
    } finally {
      setAdding(false);
    }
  };

  const saveEdit = async (guest) => {
    try {
      await editGuest(weddingId, guest.id, {
        name: draft.name?.trim() || guest.name,
        email: (draft.email ?? guest.email ?? "").trim(),
        side: draft.side || guest.side,
        household: draft.household?.trim() || null,
        age_band: draft.age_band || guest.age_band || "adult",
      });
      setEditingId(null);
      toast.success(t("guests.updated"));
      await onChanged();
    } catch (error) {
      toast.error(errorMessage(error, t("guests.cannotUpdate")));
    }
  };

  const remove = async (guest) => {
    try {
      await deleteGuest(weddingId, guest.id);
      toast.success(tf("guests.removed", { name: guest.name }));
      await onChanged();
    } catch (error) {
      toast.error(errorMessage(error, t("guests.cannotRemove")));
    }
  };

  const answered = guests.filter((g) => g.answers).length;

  return (
    <div className="space-y-6">
      <section className="surface rounded-xl p-5" data-testid="guests-spreadsheet">
        <p className="eyebrow">{t("guests.spreadsheet")}</p>
        <h2 className="mt-1 text-xl font-semibold">{t("guests.workFromExcel")}</h2>
        <p className="mt-1 text-[13px] text-[color:var(--ink-soft)]">{t("guests.excelHint")}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" asChild data-testid="guests-template-button">
            <a href={guestTemplateUrl()} download>
              <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" /> {t("guests.template")}
            </a>
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="guests-export-button">
            <a href={guestExportUrl(weddingId)} download>
              <Download className="mr-1.5 h-3.5 w-3.5" /> {tf("guests.export", { n: guests.length })}
            </a>
          </Button>
          <Button
            size="sm"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            data-testid="guests-import-button"
          >
            {uploading ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="mr-1.5 h-3.5 w-3.5" />
            )}
            {t("guests.upload")}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={onUpload}
            data-testid="guests-import-input"
          />
        </div>
        {importReport ? (
          <div
            className="mt-3 rounded-lg p-3 text-[12.5px]"
            style={{ backgroundColor: "var(--paper-2)" }}
            data-testid="guests-import-report"
          >
            <p className="font-semibold">
              {tf("guests.importReport", { added: importReport.added, updated: importReport.updated })}
              {importReport.children ? ` · ${tf("guests.children", { n: importReport.children })}` : ""}
            </p>
            {(importReport.problems || []).map((problem, i) => (
              <p key={i} className="mt-0.5 text-[color:var(--warning)]">
                {problem}
              </p>
            ))}
          </div>
        ) : null}
      </section>

      <section className="surface rounded-xl p-5">
        <p className="eyebrow">{t("guests.inviteList")}</p>
        <h2 className="mt-1 text-xl font-semibold">{t("guests.addGuest")}</h2>
        <p className="mt-1 text-[13px] text-[color:var(--ink-soft)]">
          {t("guests.addHint")}{" "}
          <span className="font-num break-all text-[11.5px]">{rsvpUrl}</span>
        </p>
        <form
          onSubmit={submitNew}
          className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.3fr_1fr_1fr_1fr_auto]"
        >
          <div>
            <SmallLabel>{t("guests.firstName")}</SmallLabel>
            <Input
              value={newGuest.first_name}
              onChange={(event) => setNewGuest((p) => ({ ...p, first_name: event.target.value }))}
              placeholder="Sofia"
              className="mt-1 h-10 bg-white"
              data-testid="new-guest-first-name-input"
            />
          </div>
          <div>
            <SmallLabel>{t("guests.lastName")}</SmallLabel>
            <Input
              value={newGuest.last_name}
              onChange={(event) => setNewGuest((p) => ({ ...p, last_name: event.target.value }))}
              placeholder="Marín"
              className="mt-1 h-10 bg-white"
              data-testid="new-guest-last-name-input"
            />
          </div>
          <div>
            <SmallLabel>
              {t("guests.email")}{" "}
              <span className="normal-case tracking-normal">· {t("guests.emailHint")}</span>
            </SmallLabel>
            <Input
              type="email"
              value={newGuest.email}
              onChange={(event) => setNewGuest((p) => ({ ...p, email: event.target.value }))}
              placeholder="sofia@example.fr"
              className="mt-1 h-10 bg-white"
              data-testid="new-guest-email-input"
            />
          </div>
          <div>
            <SmallLabel>{t("guests.side")}</SmallLabel>
            <Select
              value={newGuest.side}
              onValueChange={(value) => setNewGuest((p) => ({ ...p, side: value }))}
            >
              <SelectTrigger className="mt-1 h-10 bg-white" data-testid="new-guest-side-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SIDE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <SmallLabel>{t("guests.household")}</SmallLabel>
            <Input
              value={newGuest.household}
              onChange={(event) => setNewGuest((p) => ({ ...p, household: event.target.value }))}
              placeholder={t("guests.householdPlaceholder")}
              className="mt-1 h-10 bg-white"
              data-testid="new-guest-household-input"
            />
          </div>
          <div>
            <SmallLabel>{t("guests.generation")}</SmallLabel>
            <Select
              value={newGuest.age_band}
              onValueChange={(value) => setNewGuest((p) => ({ ...p, age_band: value }))}
            >
              <SelectTrigger className="mt-1 h-10 bg-white" data-testid="new-guest-age-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(AGE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              disabled={adding}
              className="h-10 w-full lg:w-auto"
              data-testid="add-guest-button"
            >
              {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              {t("guests.add")}
            </Button>
          </div>
        </form>
      </section>

      <section className="surface rounded-xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-semibold">
            {tf("guests.count", { n: guests.length })}{" "}
            <span className="text-[13px] font-normal text-[color:var(--muted-ink)]">
              {tf("guests.countAnswered", { n: answered })}
            </span>
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-[color:var(--muted-ink)]" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("guests.search")}
                className="h-9 w-44 bg-white pl-8 text-[13px]"
                data-testid="guests-search-input"
              />
            </div>
            {FILTERS.map((item) => (
              <Button
                key={item.id}
                size="sm"
                variant={filter === item.id ? "default" : "outline"}
                className="h-9 rounded-full px-3 text-[12px]"
                onClick={() => setFilter(item.id)}
                data-testid={`guests-filter-${item.id}`}
              >
                {t(item.label)}
              </Button>
            ))}
          </div>
        </div>

        <div className="mt-4 divide-y divide-[color:var(--line)]">
          {selected.size ? (
            <div
              className="sticky top-[76px] z-20 mb-2 flex flex-wrap items-center gap-2 rounded-lg px-3 py-2"
              style={{ backgroundColor: "var(--paper-2)", border: "1px solid var(--line)" }}
              data-testid="bulk-bar"
            >
              <span className="font-num text-[12.5px] font-semibold">
                {tf("guests.selected", { n: selected.size })}
              </span>

              <Select
                onValueChange={(value) =>
                  runBulk(
                    (ids) => bulkUpdateGuests(weddingId, ids, { side: value }),
                    (r) => tf("guests.movedTo", { n: r.updated, side: SIDE_LABELS[value] })
                  )
                }
              >
                <SelectTrigger className="h-8 w-[150px] bg-white text-[12.5px]" data-testid="bulk-side">
                  <SelectValue placeholder={t("guests.setSide")} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SIDE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                onValueChange={(value) =>
                  runBulk(
                    (ids) => bulkUpdateGuests(weddingId, ids, { role: value }),
                    (r) =>
                      value === "none"
                        ? tf("guests.backToOrdinary", { n: r.updated })
                        : tf("guests.markedAs", { n: r.updated, role: ROLE_LABELS[value] })
                  )
                }
              >
                <SelectTrigger className="h-8 w-[160px] bg-white text-[12.5px]" data-testid="bulk-role">
                  <SelectValue placeholder={t("guests.setRole")} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                  <SelectItem value="none">{t("guests.noRole")}</SelectItem>
                </SelectContent>
              </Select>

              <Input
                placeholder={t("guests.householdName")}
                className="h-8 w-[170px] bg-white text-[12.5px]"
                data-testid="bulk-household"
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  const value = event.target.value.trim();
                  event.target.value = "";
                  runBulk(
                    (ids) => bulkUpdateGuests(weddingId, ids, { household: value }),
                    (r) =>
                      value
                        ? tf("guests.groupedAs", { n: r.updated, name: value })
                        : tf("guests.ungrouped", { n: r.updated })
                  );
                }}
              />

              <Button
                variant="outline"
                size="sm"
                className="h-8"
                disabled={bulkBusy}
                onClick={() => setConfirmDelete(true)}
                data-testid="bulk-delete"
                style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> {t("guests.delete")}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="h-8"
                onClick={() => setSelected(new Set())}
                data-testid="bulk-clear"
              >
                {t("guests.clear")}
              </Button>
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            </div>
          ) : null}

          <label
            className="mb-1 flex cursor-pointer items-center gap-2 py-1 text-[12.5px] text-[color:var(--muted-ink)]"
            data-testid="select-all-row"
          >
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={toggleAllFiltered}
              className="h-3.5 w-3.5"
              data-testid="select-all-checkbox"
            />
            {t("guests.selectAll")}{" "}
            {filtered.length !== guests.length ? tf("guests.shown", { n: filtered.length }) : ""}
          </label>

          {filtered.map((guest) => {
            const editing = editingId === guest.id;
            return (
              <div key={guest.id} className="py-2.5" data-testid={`guest-row-${guest.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selected.has(guest.id)}
                    onChange={() => toggleOne(guest.id)}
                    className="h-3.5 w-3.5 shrink-0"
                    aria-label={tf("guests.select", { name: guest.name })}
                    data-testid={`select-guest-${guest.id}`}
                  />
                  <span
                    className="font-num flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                    style={{
                      backgroundColor: guest.answers ? "#f2f6f3" : "#f1efea",
                      color: guest.answers ? "var(--sage-dark)" : "var(--muted-ink)",
                    }}
                  >
                    {initials(guest.name)}
                  </span>
                  {guest.role ? (
                    <span
                      className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      style={{ backgroundColor: "#f7f1e6", color: "var(--gild)" }}
                      data-testid={`guest-role-${guest.id}`}
                    >
                      {ROLE_LABELS[guest.role] || guest.role}
                    </span>
                  ) : null}

                  {editing ? (
                    <>
                      <Input
                        value={draft.name ?? guest.name}
                        onChange={(event) => setDraft((p) => ({ ...p, name: event.target.value }))}
                        className="h-9 w-44 bg-white"
                        data-testid={`edit-name-${guest.id}`}
                      />
                      <Input
                        type="email"
                        value={draft.email ?? guest.email ?? ""}
                        onChange={(event) => setDraft((p) => ({ ...p, email: event.target.value }))}
                        placeholder={t("guests.email")}
                        className="h-9 w-48 bg-white"
                        data-testid={`edit-email-${guest.id}`}
                      />
                      <Select
                        value={draft.side ?? guest.side}
                        onValueChange={(value) => setDraft((p) => ({ ...p, side: value }))}
                      >
                        <SelectTrigger className="h-9 w-36 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(SIDE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={draft.household ?? guest.household ?? ""}
                        onChange={(event) =>
                          setDraft((p) => ({ ...p, household: event.target.value }))
                        }
                        placeholder={t("guests.householdLabel")}
                        className="h-9 w-32 bg-white"
                      />
                      <Button
                        size="sm"
                        className="h-9"
                        onClick={() => saveEdit(guest)}
                        data-testid={`save-guest-${guest.id}`}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-9" onClick={() => setEditingId(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[14px] font-medium">{guest.name}</p>
                        <p className="truncate text-[11.5px] text-[color:var(--muted-ink)]">
                          {SIDE_LABELS[guest.side] || guest.side}
                          {guest.household ? ` · ${t("guests.householdLabel")}: ${guest.household}` : ""}
                          {guest.age_band ? ` · ${AGE_LABELS[guest.age_band]}` : ""}
                          {guest.email ? ` · ${guest.email}` : ""}
                        </p>
                      </div>
                      {guest.answers ? (
                        <button
                          type="button"
                          onClick={() => setExpanded(expanded === guest.id ? null : guest.id)}
                          className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                          style={{ backgroundColor: "#f2f6f3", color: "var(--success)" }}
                          data-testid={`guest-answers-toggle-${guest.id}`}
                        >
                          {t("guests.answeredBadge")}
                        </button>
                      ) : (
                        <span
                          className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                          style={{ backgroundColor: "#f1efea", color: "var(--muted-ink)" }}
                        >
                          {t("guests.noAnswer")}
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          setEditingId(guest.id);
                          setDraft({
                            name: guest.name,
                            email: guest.email || "",
                            side: guest.side,
                            household: guest.household || "",
                            age_band: guest.age_band || "adult",
                          });
                        }}
                        data-testid={`edit-guest-${guest.id}`}
                        aria-label={tf("guests.edit", { name: guest.name })}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => remove(guest)}
                        data-testid={`delete-guest-${guest.id}`}
                        aria-label={tf("guests.remove", { name: guest.name })}
                      >
                        <Trash2 className="h-3.5 w-3.5" style={{ color: "var(--danger)" }} />
                      </Button>
                    </>
                  )}
                </div>

                {expanded === guest.id && guest.answers ? (
                  <div
                    className="mt-2 space-y-1.5 rounded-lg surface-2 p-3"
                    data-testid={`guest-answers-${guest.id}`}
                  >
                    {[
                      [t("guests.comingWith"), guest.answers.attending_with],
                      [t("guests.dietary"), guest.answers.dietary],
                      [t("guests.ownWords"), guest.answers.interests],
                      [t("guests.wantsNear"), guest.answers.seat_near],
                      [t("guests.mustNot"), guest.answers.avoid],
                    ]
                      .filter(([, value]) => value)
                      .map(([label, value]) => (
                        <p key={label} className="text-[12.5px] leading-snug">
                          <span className="font-semibold text-[color:var(--muted-ink)]">{label}: </span>
                          {value}
                        </p>
                      ))}
                  </div>
                ) : null}
              </div>
            );
          })}
          {!filtered.length ? (
            <p className="py-6 text-center text-[13px] text-[color:var(--muted-ink)]">
              {guests.length ? t("guests.noMatch") : t("guests.none")}
            </p>
          ) : null}
        </div>
      </section>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">
              {tf("guests.removeTitle", { n: selected.size })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("guests.removeHint")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">{t("guests.keepThem")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="bulk-delete-confirm"
              onClick={() =>
                runBulk(
                  (ids) => bulkDeleteGuests(weddingId, ids),
                  (r) =>
                    r.children_removed
                      ? tf("guests.removedWithChildren", { n: r.deleted, c: r.children_removed })
                      : tf("guests.removedCount", { n: r.deleted })
                )
              }
            >
              {t("guests.removeConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GuestsTab;
